import { asList, getJson, postJson } from './helpers';

function mapProjectStatus(raw) {
  if (raw == null || raw === '') return 'active';
  const status = String(raw).toLowerCase().trim();
  if (['active', 'in progress', 'open', 'on track', 'preconstruction', 'bidding'].includes(status)) return 'active';
  if (['completed', 'complete', 'closed'].includes(status)) return 'completed';
  if (['on hold', 'on-hold', 'hold', 'suspended'].includes(status)) return 'on-hold';
  if (['cancelled', 'canceled', 'void', 'terminated'].includes(status)) return 'cancelled';
  return 'active';
}

function projectNumber(project) {
  const raw = project?.project_number ?? project?.number ?? project?.code;
  if (raw == null) return '';
  return String(raw).trim();
}

function moneyLabel(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount === 0) return '';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function item(id, title, subtitle, raw, extra = {}) {
  return {
    id: String(id),
    title: title || String(id),
    subtitle: subtitle || '',
    alreadyImported: Boolean(raw?.already_imported || extra.alreadyImported),
    amount: extra.amount,
    raw,
  };
}

export const SEND_PATHS = {
  project: 'send-project',
  vendor: 'send-vendor',
  customer: 'send-customer',
  employee: 'send-employee',
  commitment: 'send-commitment',
  budget: 'send-budget',
  contract: 'send-contract',
  'project-billing': 'send-project-billing',
  'qbo-project-billing': 'send-qbo-project-billing',
  'direct-cost': 'send-direct-cost',
  'ap-invoice': 'send-ap-invoice',
  'journal-entry': 'send-journal-entry',
};

export async function loadActiveProcoreIntegrations() {
  const data = await getJson('/api/integrations');
  return asList(data).filter(
    (row) => String(row.integration_type || '').toLowerCase() === 'procore' && row.status === 'active'
  );
}

export async function sendToProcore(integrationVuid, sendKind, payload) {
  const path = SEND_PATHS[sendKind];
  if (!path) {
    throw new Error(`Send is not available for this record.`);
  }
  const data = await postJson(`/api/integrations/${integrationVuid}/${path}`, payload || {});
  if (data && data.success === false) {
    const err = new Error(data.error || data.message || 'Failed to send to Procore');
    err.response = { data };
    throw err;
  }
  return data;
}

export async function listFromProcore(kind, integrationVuid, projectVuid) {
  const warnings = [];
  let items = [];

  if (kind === 'projects') {
    const data = await getJson(`/api/integrations/${integrationVuid}/retrieve-projects`);
    items = (data.projects || []).map((project) =>
      item(project.id, project.name || project.display_name, projectNumber(project) || project.company?.name, project)
    );
  } else if (kind === 'employees') {
    const data = await getJson(`/api/integrations/${integrationVuid}/retrieve-employees`);
    items = (data.employees || [])
      .filter((employee) => !employee.already_imported)
      .map((employee) =>
        item(
          employee.procore_user_id,
          employee.employee_name,
          employee.email || employee.login,
          employee
        )
      );
  } else if (kind === 'commitments') {
    const data = await getJson(`/api/integrations/${integrationVuid}/retrieve-commitments`, {
      project_vuid: projectVuid,
    });
    items = (data.commitments || []).map((commitment) => {
      const woc = commitment.work_order_contract || commitment;
      const vendor = woc.vendor;
      const vendorName = typeof vendor === 'string' ? vendor : vendor?.name || vendor?.vendor_name || '';
      const contractLabel = commitment.procore_contract_type === 'purchase_order_contract' ? 'Purchase order' : '';
      return item(
        woc.id || woc.contract_id || commitment.id,
        [woc.number, woc.name || woc.title].filter(Boolean).join(' — '),
        [vendorName, contractLabel].filter(Boolean).join(' · '),
        commitment,
        { amount: woc.total || woc.grand_total || commitment.total_amount }
      );
    });
  } else if (kind === 'prime-change-orders') {
    const data = await getJson(`/api/integrations/${integrationVuid}/list-prime-change-orders`, {
      project_vuid: projectVuid,
    });
    items = (data.change_orders || []).map((row) =>
      item(
        row.id,
        [row.change_order_number || row.number, row.description || row.name || row.title].filter(Boolean).join(' — '),
        row.status,
        row,
        { amount: row.total_amount || row.grand_total || row.amount }
      )
    );
    (data.errors || []).forEach((warning) => warnings.push(warning));
  } else if (kind === 'internal-change-orders') {
    const data = await getJson(`/api/integrations/${integrationVuid}/list-internal-change-orders`, {
      project_vuid: projectVuid,
    });
    items = (data.change_orders || []).map((row) =>
      item(
        row.id || row.change_order_number,
        [row.change_order_number || row.number, row.description || row.name].filter(Boolean).join(' — '),
        row.status,
        row,
        {
          amount:
            row.total_amount ||
            row.amount ||
            (row.line_items || []).reduce((sum, line) => sum + Number(line.change_amount || 0), 0),
        }
      )
    );
    (data.errors || []).forEach((warning) => warnings.push(warning));
  } else if (kind === 'commitment-change-orders') {
    const data = await getJson(`/api/integrations/${integrationVuid}/list-commitment-change-orders`, {
      project_vuid: projectVuid,
    });
    items = (data.change_orders || []).map((row) =>
      item(
        row.id,
        [row.change_order_number, row.description].filter(Boolean).join(' — '),
        row.commitment_name || row.status,
        row,
        { amount: row.total_amount }
      )
    );
    (data.errors || []).forEach((warning) => warnings.push(warning));
  } else if (kind === 'ap-invoices') {
    const data = await getJson(`/api/integrations/${integrationVuid}/list-ap-invoices`, {
      project_vuid: projectVuid,
    });
    items = (data.invoices || []).map((invoice) =>
      item(
        invoice.id || invoice.requisition_id,
        invoice.invoice_number || `Requisition ${invoice.requisition_id}`,
        [invoice.vendor?.name, invoice.project_name].filter(Boolean).join(' · '),
        invoice,
        {
          amount:
            invoice.subtotal ||
            (Number(invoice.total_amount || 0) + Number(invoice.retention_held || 0)),
        }
      )
    );
    (data.errors || []).forEach((warning) => warnings.push(warning));
  } else if (kind === 'budget-lines') {
    const data = await getJson(`/api/integrations/${integrationVuid}/retrieve-budget-lines`, {
      project_vuid: projectVuid,
    });
    items = (data.budget_lines || []).map((line) => {
      const code = line.cost_code?.code || '';
      const type = line.cost_type?.name || line.cost_type?.abbreviation || '';
      return item(
        line.id,
        [code, type].filter(Boolean).join(' · ') || `Budget line ${line.id}`,
        line.notes || (line.already_imported ? 'Already imported' : ''),
        line,
        { amount: line.budget_amount, alreadyImported: line.already_imported }
      );
    });
  } else if (kind === 'labor-costs') {
    const data = await getJson(`/api/integrations/${integrationVuid}/retrieve-labor-costs`, {
      project_vuid: projectVuid,
    });
    items = (data.labor_costs || []).map((row, index) => {
      const hoursLabel = row.hours != null && row.hours !== '' ? `${row.hours} hrs` : '';
      return item(
        [
          row.procore_timesheet_id,
          row.employee_id || row.employee_vuid || row.procore_user_id,
          row.date,
          row.cost_code_vuid,
          index,
        ]
          .filter((part) => part != null && part !== '')
          .join(':'),
        row.employee_name || row.employee_id || `Timesheet ${row.procore_timesheet_id || index + 1}`,
        [row.date, row.cost_code || row.cost_code_name, hoursLabel].filter(Boolean).join(' · '),
        row,
        { amount: row.amount }
      );
    });
    (data.errors || []).forEach((warning) => warnings.push(warning));
  } else {
    throw new Error('Retrieve is not available for this tool.');
  }

  return { items, warnings };
}

export async function importFromProcore(kind, integrationVuid, projectVuid, selected) {
  if (!selected.length) {
    throw new Error('Select at least one record to import.');
  }

  if (kind === 'projects') {
    return importProjects(integrationVuid, selected);
  }
  if (kind === 'employees') {
    const data = await postJson(`/api/integrations/${integrationVuid}/import-employees`, {
      employees: selected.map((row) => row.raw),
    });
    if (data && data.success === false) {
      const err = new Error(data.error || 'Failed to import employees');
      err.response = { data };
      throw err;
    }
    const created = data.employees_created || 0;
    const updated = data.employees_updated || 0;
    return {
      message: `Imported employees — created ${created}, updated ${updated}.`,
      errors: data.errors || [],
    };
  }
  if (kind === 'commitments') {
    return importCommitments(integrationVuid, projectVuid, selected);
  }
  if (kind === 'prime-change-orders') {
    const data = await postJson(`/api/integrations/${integrationVuid}/import-prime-change-orders`, {
      project_vuid: projectVuid,
      change_orders: selected.map((row) => row.raw),
    });
    return summarizeImport(data, 'prime change order');
  }
  if (kind === 'internal-change-orders') {
    const data = await postJson(`/api/integrations/${integrationVuid}/import-internal-change-orders`, {
      project_vuid: projectVuid,
      change_orders: selected.map((row) => row.raw),
    });
    return summarizeImport(data, 'internal change order');
  }
  if (kind === 'commitment-change-orders') {
    const data = await postJson(`/api/integrations/${integrationVuid}/import-commitment-change-orders`, {
      project_vuid: projectVuid,
      change_order_ids: selected.map((row) => row.raw.id),
      change_orders: selected.map((row) => ({
        id: row.raw.id,
        commitment_vuid: row.raw.commitment_vuid,
      })),
    });
    return summarizeImport(data, 'commitment change order');
  }
  if (kind === 'ap-invoices') {
    const requisitionIds = selected.map((row) => row.raw.requisition_id).filter(Boolean);
    const data = await postJson(`/api/integrations/${integrationVuid}/retrieve-ap-invoices`, {
      requisition_ids: requisitionIds,
    });
    if (data && data.success === false) {
      const err = new Error(data.error || 'Failed to import AP invoices');
      err.response = { data };
      throw err;
    }
    return {
      message: `Imported ${data.count || 0} AP invoice(s).`,
      errors: data.errors || [],
    };
  }
  if (kind === 'budget-lines') {
    return importBudgetLines(integrationVuid, projectVuid, selected);
  }
  if (kind === 'labor-costs') {
    const data = await postJson(`/api/integrations/${integrationVuid}/import-labor-costs`, {
      labor_costs: selected.map((row) => row.raw),
    });
    if (data && data.success === false) {
      const err = new Error(data.error || 'Failed to import labor costs');
      err.response = { data };
      throw err;
    }
    const created = data.labor_costs_created || 0;
    const updated = data.labor_costs_updated || 0;
    return {
      message: `Imported labor costs — created ${created}, updated ${updated}.`,
      errors: data.errors || [],
    };
  }
  throw new Error('Import is not available for this tool.');
}

function summarizeImport(data, label) {
  if (data && data.success === false) {
    const err = new Error(data.error || `Failed to import ${label}s`);
    err.response = { data };
    throw err;
  }
  const imported = Array.isArray(data.imported) ? data.imported.length : data.count || 0;
  return {
    message: `Imported ${imported} ${label}(s).`,
    errors: data.errors || [],
  };
}

async function importProjects(integrationVuid, selected) {
  const errors = [];
  let imported = 0;
  for (const row of selected) {
    const project = row.raw;
    try {
      const created = await postJson('/api/projects', {
        project_number: projectNumber(project) || `IMPORT-${project.id}`,
        project_name: project.name,
        description: project.description || 'Imported from Procore',
        start_date: project.start_date,
        end_date: project.end_date,
        status: mapProjectStatus(project.status),
      });
      const newProject = created?.project || created;
      if (integrationVuid && project.id && newProject?.vuid) {
        try {
          await postJson('/api/external-system-ids', {
            integration_vuid: integrationVuid,
            object_type: 'project',
            project_vuid: newProject.vuid,
            object_vuid: newProject.vuid,
            external_id: String(project.id),
            external_object_type: 'project',
            external_metadata: {
              name: project.name,
              project_number: project.project_number,
              number: project.number,
              display_name: project.display_name,
              company: project.company,
            },
            external_status: project.status || 'active',
          });
        } catch (mappingError) {
          errors.push(`${project.name || project.id}: imported, but mapping failed — ${mappingError.response?.data?.error || mappingError.message}`);
        }
      }
      imported += 1;
    } catch (error) {
      errors.push(`${project.name || project.id}: ${error.response?.data?.error || error.message}`);
    }
  }
  return { message: `Imported ${imported} project(s).`, errors };
}

async function importCommitments(integrationVuid, projectVuid, selected) {
  const vendors = asList(await getJson('/api/vendors').catch(() => []));
  const errors = [];
  let imported = 0;
  for (const row of selected) {
    const commitment = row.raw;
    const woc = commitment.work_order_contract || {};
    const commitmentNumber = woc.number || commitment.number;
    const commitmentTitle = woc.name || commitment.title || woc.title;
    const commitmentAmount = woc.total || commitment.total_amount || woc.grand_total || 0;
    let vendorName = '';
    if (typeof commitment.vendor === 'string') vendorName = commitment.vendor;
    else if (typeof woc.vendor === 'string') vendorName = woc.vendor;
    else if (woc.vendor && typeof woc.vendor === 'object') vendorName = woc.vendor.name || woc.vendor.vendor_name || '';

    let vendor = null;
    if (woc.vendor_vuid) vendor = vendors.find((item) => item.vuid === woc.vendor_vuid);
    if (!vendor && vendorName) {
      const needle = vendorName.toLowerCase();
      vendor = vendors.find(
        (item) =>
          String(item.vendor_name || '').toLowerCase().includes(needle) ||
          needle.includes(String(item.vendor_name || '').toLowerCase())
      );
    }
    if (!vendor && vendors.length) vendor = vendors[0];
    if (!vendor) {
      errors.push(`${commitmentNumber}: no vendors available to map`);
      continue;
    }
    try {
      const created = await postJson('/api/project-commitments', {
        project_vuid: projectVuid,
        commitment_number: commitmentNumber || 'Unknown',
        commitment_name: commitmentTitle || 'Imported Commitment',
        vendor_vuid: vendor.vuid,
        commitment_date: new Date().toISOString().split('T')[0],
        original_amount: commitmentAmount,
        retainage_percentage:
          woc.retainage_percentage != null && woc.retainage_percentage !== ''
            ? parseFloat(woc.retainage_percentage)
            : woc.retainage_percent != null && woc.retainage_percent !== ''
              ? parseFloat(woc.retainage_percent)
              : undefined,
        status: 'active',
        description: `Imported from Procore - Original vendor: ${vendorName || 'Unknown'}`,
      });
      const commitmentVuid = created.vuid || created.commitment?.vuid;
      if (!commitmentVuid) {
        errors.push(`${commitmentNumber}: import response missing vuid`);
        continue;
      }
      const procoreCommitmentId = woc.contract_id != null ? woc.contract_id : woc.id;
      const contractType = commitment.procore_contract_type || 'work_order_contract';
      const contractIdKey = contractType === 'purchase_order_contract'
        ? 'purchase_order_contract_id'
        : 'work_order_contract_id';
      if (procoreCommitmentId) {
        try {
          await postJson('/api/external-system-ids', {
            integration_vuid: integrationVuid,
            object_type: 'commitment',
            project_vuid: projectVuid,
            object_vuid: commitmentVuid,
            external_id: String(procoreCommitmentId),
            external_object_type: contractType,
            external_metadata: {
              commitment_number: commitmentNumber,
              commitment_name: commitmentTitle,
              retainage_percentage: woc.retainage_percentage,
              [contractIdKey]: woc.id != null ? String(woc.id) : undefined,
            },
            external_status: 'active',
          });
          if (woc.id) {
            try {
              await postJson(`/api/integrations/${integrationVuid}/write-commitment-origin-id`, {
                commitment_vuid: commitmentVuid,
                procore_contract_type: contractType,
                [contractIdKey]: String(woc.id),
                procore_project_id: commitment.procore_project_id,
              });
            } catch (originWriteError) {
              errors.push(`${commitmentNumber}: imported, but origin_id write failed — ${originWriteError.response?.data?.error || originWriteError.message}`);
            }
          }
        } catch (mappingError) {
          errors.push(`${commitmentNumber}: imported, but mapping failed — ${mappingError.response?.data?.error || mappingError.message}`);
        }
      }
      const lines = commitment.work_order_contract_lines || [];
      for (const line of lines) {
        if (!line.cost_code_vuid || !line.cost_type_vuid) continue;
        try {
          await postJson(`/api/project-commitments/${commitmentVuid}/items`, {
            commitment_vuid: commitmentVuid,
            cost_code_vuid: line.cost_code_vuid,
            cost_type_vuid: line.cost_type_vuid,
            description: line.description || 'Imported line item',
            quantity: line.quantity || 1,
            unit_price: line.unit_cost || (line.amount / (line.quantity || 1)),
            total_amount: line.amount || 0,
          });
        } catch (lineError) {
          errors.push(`${commitmentNumber} line: ${lineError.response?.data?.error || lineError.message}`);
        }
      }
      imported += 1;
    } catch (error) {
      errors.push(`${commitmentNumber}: ${error.response?.data?.error || error.message}`);
    }
  }
  return { message: `Imported ${imported} commitment(s).`, errors };
}

async function importBudgetLines(integrationVuid, projectVuid, selected) {
  try {
    await postJson(`/api/integrations/${integrationVuid}/refresh-wbs-codes`, { project_vuid: projectVuid });
  } catch (error) {
    // Continue with whatever cost codes already exist locally.
  }
  const [costTypes, settings, budgets, periods] = await Promise.all([
    getJson('/api/costtypes').catch(() => []),
    getJson(`/api/projects/${projectVuid}/settings`).catch(() => ({})),
    getJson('/api/project-budgets', { project_vuid: projectVuid }).catch(() => []),
    getJson('/api/accounting-periods').catch(() => []),
  ]);
  const allowProject = settings?.allow_project_cost_codes === true;
  const typesForMatch = asList(costTypes);
  const codeCache = {};
  const matchCostCode = async (code) => {
    if (!code) return null;
    if (Object.prototype.hasOwnProperty.call(codeCache, code)) return codeCache[code];
    const data = allowProject
      ? await getJson(`/api/projects/${projectVuid}/cost-codes`, {
          scope: 'project',
          q: code,
          page: 1,
          per_page: 25,
        }).catch(() => ({ items: [] }))
      : await getJson('/api/cost-codes', {
          q: code,
          page: 1,
          per_page: 25,
          status: 'active',
        }).catch(() => ({ items: [] }));
    const found = asList(data).find(
      (row) => row.code === code && (row.status === 'active' || !row.status)
    ) || null;
    codeCache[code] = found;
    return found;
  };
  const projectBudgets = asList(budgets).filter((row) => row.project_vuid === projectVuid);
  let budgetVuid = projectBudgets.find((row) => row.budget_type === 'original')?.vuid || projectBudgets[0]?.vuid;
  if (!budgetVuid) {
    const openPeriod = asList(periods).find((row) => row.status === 'open');
    if (!openPeriod) {
      throw new Error('No open accounting period found. Create one before importing budget lines.');
    }
    const created = await postJson('/api/project-budgets', {
      project_vuid: projectVuid,
      accounting_period_vuid: openPeriod.vuid,
      description: 'Imported Budget from Procore',
      budget_type: 'original',
      budget_date: new Date().toISOString().split('T')[0],
      finalized: false,
    });
    budgetVuid = created?.vuid;
    if (!budgetVuid) throw new Error('Failed to create a budget for imported lines.');
  }

  const errors = [];
  let imported = 0;
  for (const row of selected) {
    const line = row.raw;
    if (line.already_imported) continue;
    const costCodeToMatch = line.cost_code?.code || line.cost_code || '';
    const costTypeToMatch = line.cost_type?.name || line.cost_type || '';
    const matchingCostCode = await matchCostCode(costCodeToMatch);
    const matchingCostType = typesForMatch.find(
      (type) => type.cost_type === costTypeToMatch && (type.status === 'active' || !type.status)
    );
    if (!matchingCostCode || !matchingCostType) {
      errors.push(
        `${costCodeToMatch || 'line'} / ${costTypeToMatch || 'type'}: no matching Claret cost code or cost type`
      );
      continue;
    }
    try {
      const payload = {
        cost_code_vuid: matchingCostCode.vuid,
        cost_type_vuid: matchingCostType.vuid,
        budget_amount: parseFloat(line.budget_amount) || 0,
        quantity: line.quantity,
        unit_of_measure: line.unit_of_measure,
        unit_price: line.unit_price,
        notes: line.notes || '',
        status: 'active',
      };
      if (line.wbs_code_id) {
        payload.integration_vuid = integrationVuid;
        payload.procore_wbs_code_id = String(line.wbs_code_id);
      }
      const created = await postJson(`/api/project-budgets/${budgetVuid}/lines`, payload);
      if (created?.vuid && line.id) {
        try {
          await postJson('/api/external-system-ids', {
            object_vuid: created.vuid,
            object_type: 'budget_line_item',
            integration_vuid: integrationVuid,
            external_id: String(line.id),
            external_object_type: 'budget_line_item',
          });
        } catch (mappingError) {
          errors.push(`${costCodeToMatch}: imported, but mapping failed — ${mappingError.response?.data?.error || mappingError.message}`);
        }
      }
      imported += 1;
    } catch (error) {
      errors.push(`${costCodeToMatch || 'line'}: ${error.response?.data?.error || error.message}`);
    }
  }
  return { message: `Imported ${imported} budget line(s).`, errors };
}

export function retrieveLabel(kind) {
  return {
    projects: 'Retrieve projects from Procore',
    employees: 'Retrieve employees from Procore',
    commitments: 'Retrieve commitments from Procore',
    'prime-change-orders': 'Retrieve prime COs',
    'internal-change-orders': 'Retrieve internal COs',
    'commitment-change-orders': 'Retrieve commitment COs',
    'ap-invoices': 'Retrieve AP invoices from Procore',
    'budget-lines': 'Retrieve budget from Procore',
  }[kind] || 'Retrieve from Procore';
}

export function formatItemAmount(value) {
  return moneyLabel(value);
}
